import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Heart, RefreshCw, Sparkles, Zap } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { getH1 } from '../seo/seoRoutes.mjs';
import { translations, DEFAULT_LANGUAGE } from '../utils/languages';

const MentalHealingSpreadPage = () => {
  const { t, currentLanguage } = useLanguage();
  const navigate = useNavigate();
  const [selectedCardCount, setSelectedCardCount] = useState(null);
  const [selectedHealingType, setSelectedHealingType] = useState(null);
  const [question, setQuestion] = useState('');
  const [currentQuote, setCurrentQuote] = useState('');

  // Card count options with their meanings for healing
  const cardOptions = [
    { count: 1, name: t('healing.singleHealingFocus'), description: t('healing.singleHealingFocusDesc') },
    { count: 3, name: t('healing.healingTriangle'), description: t('healing.healingTriangleDesc') },
    { count: 5, name: t('healing.healingCross'), description: t('healing.healingCrossDesc') },
    { count: 6, name: t('healing.holisticHealing'), description: t('healing.holisticHealingDesc') },
    { count: 7, name: t('healing.chakraHealing'), description: t('healing.chakraHealingDesc') },
    { count: 10, name: t('healing.deepHealingJourney'), description: t('healing.deepHealingJourneyDesc') },
    { count: 12, name: t('healing.yearOfHealing'), description: t('healing.yearOfHealingDesc') }
  ];

  // Healing type options
  const healingTypes = [
    {
      type: "self-healing",
      name: t('healing.selfHealing'),
      icon: "icon_07_sh_v2.png",
      color: "#E7FBBE",
      description: t('healing.selfHealingDesc'),
      focusAreas: [t('healing.selfCompassion'), t('healing.innerChildWork'), t('healing.personalBoundaries'), t('healing.selfForgiveness')]
    },
    {
      type: "energy-healing",
      name: t('healing.energyHealing'),
      icon: "icon_08_enh_v2.png",
      color: "#FFFDDE",
      description: t('healing.energyHealingDesc'),
      focusAreas: [t('healing.chakraAlignment'), t('healing.auraCleansing'), t('healing.energyBlocks'), t('healing.spiritualProtection')]
    },
    {
      type: "soulmate-healing",
      name: t('healing.soulmateHealing'),
      icon: "icon_05_col_v2.png",
      color: "#FFCBCB",
      description: t('healing.soulmateHealingDesc'),
      focusAreas: [t('healing.soulContracts'), t('healing.twinFlameHealing'), t('healing.karmicRelationships'), t('healing.divineLove')]
    },
    {
      type: "pregnancy",
      name: t('healing.pregnancyFertility'),
      icon: "icon_03_family_v2.png",
      color: "#D9D7F1",
      description: t('healing.pregnancyFertilityDesc'),
      focusAreas: [t('healing.fertilityJourney'), t('healing.pregnancySupport'), t('healing.maternalHealing'), t('healing.newLifeEnergy')]
    },
    {
      type: "physical-healing",
      name: t('healing.physicalHealing'),
      icon: "icon_09_phy_v2.png",
      color: "#E7FBBE",
      description: t('healing.physicalHealingDesc'),
      focusAreas: [t('healing.bodyWisdom'), t('healing.healingEnergy'), t('healing.vitalityRestoration'), t('healing.physicalStrength')]
    },
    {
      type: "rejection-healing",
      name: t('healing.rejectionHealing'),
      icon: "icon_10_rej_v2.png",
      color: "#FFFDDE",
      description: t('healing.rejectionHealingDesc'),
      focusAreas: [t('healing.selfWorthHealing'), t('healing.rejectionRecovery'), t('healing.confidenceRebuilding'), t('healing.trustRestoration')]
    }
  ];

  // Spread interpretations using translation keys
  const getSpreadInterpretations = (type, count) => {
    // Map UI healing types to translation key types
    const typeMapping = {
      'self-healing': 'selfHealing',
      'energy-healing': 'energyHealing',
      'soulmate-healing': 'soulmateHealing',
      'pregnancy': 'pregnancy',
      'physical-healing': 'physicalHealing',
      'rejection-healing': 'rejectionHealing'
    };

    const healingType = typeMapping[type] || type;
    const positions = [];

    // For each position in the spread, get the translated name
    for (let i = 1; i <= count; i++) {
      const specificKey = `healing.spread.${healingType}.${count}.position${i}`;
      const baseKey = `healing.spread.base.${count}.position${i}`;
      const translation = translations[currentLanguage]?.[specificKey] || 
                         translations[currentLanguage]?.[baseKey] ||
                         translations[DEFAULT_LANGUAGE]?.[specificKey] ||
                         translations[DEFAULT_LANGUAGE]?.[baseKey] ||
                         `Card ${i}`;
      positions.push(translation);
    }
    
    return positions;
  };

  // Set random quote on component mount
  useEffect(() => {
    const healingQuotes = t('quotes.healing');
    const randomQuote = healingQuotes[Math.floor(Math.random() * healingQuotes.length)];
    setCurrentQuote(randomQuote);
  }, [t]);

  // Auto-scroll to healing type section when card count is selected
  useEffect(() => {
    if (selectedCardCount) {
      const healingSection = document.querySelector('.healing-type-section');
      if (healingSection) {
        healingSection.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
    }
  }, [selectedCardCount]);

  // Auto-scroll to question section when healing type is selected
  useEffect(() => {
    if (selectedHealingType) {
      const questionSection = document.querySelector('.question-section');
      if (questionSection) {
        questionSection.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
    }
  }, [selectedHealingType]);

  const startHealingReading = () => {
    if (!selectedCardCount || !selectedHealingType) return;
    
    const healingType = healingTypes.find(t => t.type === selectedHealingType);
    
    navigate('/spread-drawing', {
      state: {
        spreadName: `${healingType.name} - ${cardOptions.find(o => o.count === selectedCardCount)?.name}`,
        cardCount: selectedCardCount,
        spreadType: "mental-healing",
        spreadInterpretations: getSpreadInterpretations(selectedHealingType, selectedCardCount),
        question: question,
        tarotCards: [], // Use default cards from SpreadDrawingPage
        returnPath: "/mental-healing-spread",
        spreadConfig: {
          healingType: selectedHealingType,
          isHealingReading: true
        }
      }
    });
  };

  const resetSpread = () => {
    setSelectedCardCount(null);
    setSelectedHealingType(null);
    setQuestion('');
    
    // Set new random quote
    const healingQuotes = t('quotes.healing');
    const randomQuote = healingQuotes[Math.floor(Math.random() * healingQuotes.length)];
    setCurrentQuote(randomQuote);
  };

  return (
    <div className="mental-healing-spread-page">
      {/* Welcome Section */}
      <section className="welcome-section">
        <div className="welcome-content">
          <div className="welcome-text">
            <h1 className="welcome-subtitle">
              {getH1('/mental-healing-spread', currentLanguage)}
            </h1>
            <blockquote className="inspiration-question">
              {currentQuote}
            </blockquote>
            <button 
              className="draw-cards-btn"
              onClick={() => {
                navigate('/spread-drawing', { 
                  state: { 
                    spreadName: t('healing.drawHealingCard'),
                    spreadType: 'single',
                    cardCount: 1,
                    spreadInterpretations: [t('dailyCard.healingPosition')],
                    question: currentQuote,
                    returnPath: "/mental-healing-spread",
                    spreadConfig: {
                      isSingleCard: true,
                      isDailyCard: true
                    }
                  } 
                })
              }}
            >
              <Sparkles size={20} />
              {t('healing.drawHealingCard')}
            </button>
          </div>
          <div className="welcome-decoration"></div>
        </div>
      </section>

      <div className="card-selection-section">
        <h2>1. {t('healing.chooseHealingSpread')}</h2>
        <p className="selection-subtitle">{t('healing.selectCardCount')}</p>
        
        <div className="card-options-grid">
          {cardOptions.map((option) => (
            <div
              key={option.count}
              className={`card-option ${selectedCardCount === option.count ? 'selected' : ''}`}
              onClick={() => setSelectedCardCount(option.count)}
            >
              <span className="card-count">{option.count}</span>
              <div className="option-content">
                <span className="option-name">{option.name}</span>
                <p className="option-description">{option.description}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Healing Type Section */}
        <div className="healing-type-section">
          <h2>2. {t('healing.healingTypeTitle')}</h2>
          <p className="selection-subtitle">{t('healing.selectHealingType')}</p>
          
          <div className="healing-types-grid">
            {healingTypes.map((healingType) => (
              <div
                key={healingType.type}
                className={`healing-type-card ${selectedHealingType === healingType.type ? 'selected' : ''}`}
                onClick={() => setSelectedHealingType(healingType.type)}
                style={{ '--type-color': healingType.color }}
              >
                <div className="type-icon">
                  <img 
                    src={`/images/${healingType.icon}`} 
                    alt={healingType.name}
                    style={{ width: '3rem', height: '3rem' }}
                  />
                </div>
                <h3 className="type-name">{healingType.name}</h3>
                <p className="type-description">{healingType.description}</p>
                <div className="focus-areas">
                  <small>{t('healing.focusAreas')}</small>
                  <ul>
                    {healingType.focusAreas.map((area, index) => (
                      <li key={index}>{area}</li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>

        {selectedCardCount && selectedHealingType && (
          <div className="question-section">
            <h2>3. {t('healing.focusHealingIntention')}</h2>
            <p className="selection-subtitle">{t('healing.healingIntentionDesc')}</p>
            <textarea
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder={t('healing.healingPlaceholder')}
              className="question-input"
              rows="3"
            />
            
            <button 
              onClick={startHealingReading} 
              className="draw-cards-btn"
            >
              <Heart size={20} />
              {t('healing.startDrawing')}
            </button>

            <button 
              onClick={resetSpread} 
              className="secondary-btn"
              style={{ marginLeft: '1rem' }}
            >
              <RefreshCw size={16} />
              {t('healing.reset')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default MentalHealingSpreadPage;
